import { useEffect, useMemo, useState } from 'react';
import { Gift, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Btn,
  IconButton,
  LoadError,
  LoadSkeleton,
  PublicationStatus,
  SectionHeading,
} from '../components/ui';
import AdminBonusPackEditor from '../components/admin/AdminBonusPackEditor';
import { useAdminBonuses } from '../hooks';
import { c } from '../styles/theme';
import type { BonusClaimRecipient } from '../types/domain';

export default function AdminBonuses() {
  const {
    bonuses,
    load,
    loadBonuses,
    getBonusClaimRecipients,
    createBonus,
    updateBonus,
    deleteBonus,
    previewBonusEmail,
    pending,
    isPending,
  } = useAdminBonuses();
  const [panelMode, setPanelMode] = useState<'create' | 'edit' | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [claimRecipients, setClaimRecipients] = useState<BonusClaimRecipient[]>([]);
  const [claimsStatus, setClaimsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const selectedBonus = useMemo(
    () => bonuses.find((bonus) => bonus.id === selectedId) ?? null,
    [bonuses, selectedId]
  );

  useEffect(() => {
    if (panelMode !== 'edit' || !selectedId) {
      setClaimRecipients([]);
      setClaimsStatus('idle');
      return;
    }
    let cancelled = false;
    setClaimRecipients([]);
    setClaimsStatus('loading');
    void getBonusClaimRecipients(selectedId)
      .then((recipients) => {
        if (cancelled) return;
        setClaimRecipients(recipients);
        setClaimsStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setClaimsStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [getBonusClaimRecipients, panelMode, selectedId]);

  if (load.status === 'loading' && bonuses.length === 0) {
    return <LoadSkeleton rows={3} rowHeight={108} label="Loading bonus packs…" />;
  }

  if (load.status === 'error' && bonuses.length === 0) {
    return (
      <LoadError
        message={load.error?.message ?? 'Could not load bonus packs.'}
        onRetry={loadBonuses}
      />
    );
  }

  return (
    <div>
      <SectionHeading
        as="h2"
        title="Bonuses"
        subtitle="Admin CRUD for Lyrixsa Bonus packs"
        marginBottom={14}
      />

      <div
        style={{
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 12,
          padding: '14px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div>
            <p style={{ fontSize: 12, color: c.text2, margin: '0 0 4px' }}>Available bonus packs</p>
            <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
              Select a bonus pack to edit it in the side panel.
            </p>
          </div>
          <Btn
            onClick={() => {
              setSelectedId('');
              setPanelMode('create');
            }}
            disabled={pending}
          >
            <Plus size={13} />
            Create bonus pack
          </Btn>
        </div>

        {bonuses.length === 0 ? (
          <p style={{ fontSize: 12, color: c.text3, margin: 0 }}>No persisted bonus packs yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bonuses.map((bonus) => (
              <div
                key={bonus.id}
                style={{
                  border: `1px solid ${selectedId === bonus.id && panelMode === 'edit' ? c.accentBorder : c.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  background:
                    selectedId === bonus.id && panelMode === 'edit' ? c.accentSoft : c.surface2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(bonus.id);
                    setPanelMode('edit');
                  }}
                  style={{
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Gift size={16} color={c.accent} />
                    <div>
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          margin: '0 0 3px',
                          color: c.accent,
                        }}
                      >
                        {bonus.name}
                      </p>
                      <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
                        {bonus.kind === 'credit-pack' && bonus.creditGrant
                          ? `${bonus.creditGrant.amount.toLocaleString('en-US')} ${bonus.creditGrant.kind === 'publisher' ? 'Publisher' : 'generation'} credits · ${bonus.creditGrant.recipientCount ?? 0} recipients · ${bonus.creditGrant.emailSentCount ?? 0} emailed${bonus.creditGrant.emailFailedCount ? ` · ${bonus.creditGrant.emailFailedCount} failed` : ''}`
                          : `${bonus.kind}${bonus.audience?.specificEmails?.length ? ` · ${bonus.audience.specificEmails.length} specific ${bonus.audience.specificEmails.length === 1 ? 'user' : 'users'}` : ''}`}
                      </p>
                    </div>
                  </div>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      color: c.text2,
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    {bonus.kind !== 'credit-pack' && (
                      <PublicationStatus published={bonus.published !== false} />
                    )}
                    <Pencil size={12} />
                    Edit
                  </span>
                </button>
                <IconButton
                  label={`Delete ${bonus.name}`}
                  disabled={pending || isPending(bonus.id)}
                  onClick={() => {
                    if (selectedId === bonus.id && panelMode === 'edit') setPanelMode(null);
                    if (selectedId === bonus.id) setSelectedId('');
                    void deleteBonus(bonus.id);
                  }}
                  style={{ color: c.danger, width: 18, height: 18, flexShrink: 0 }}
                >
                  <Trash2 size={13} />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </div>

      {panelMode && (
        <AdminBonusPackEditor
          mode={panelMode}
          pack={panelMode === 'edit' ? selectedBonus : null}
          pending={pending || (selectedBonus ? isPending(selectedBonus.id) : false)}
          claimRecipients={claimRecipients}
          claimsStatus={claimsStatus}
          onPreviewEmail={previewBonusEmail}
          onClose={() => setPanelMode(null)}
          onSave={(bonus) =>
            panelMode === 'create'
              ? createBonus(bonus)
              : updateBonus(selectedBonus?.id ?? bonus.id, bonus)
          }
          onDelete={
            panelMode === 'edit' && selectedBonus
              ? async (id) => {
                  await deleteBonus(id);
                  setSelectedId('');
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
